import { useState } from 'react';
import { App, Input, Modal, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { errMsg } from '../../lib/errors';
import { PORTAL_BASE, PORTAL_KEY } from './portalApi';

/** A POST to the portal that refreshes every portal list on success. */
export function usePortalAction<V>(path: (v: V) => string, body: (v: V) => object, success: string, failure: string) {
  const qc = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: async (v: V) => (await api.post(`${PORTAL_BASE}${path(v)}`, body(v))).data as unknown,
    onSuccess: () => {
      message.success(success);
      void qc.invalidateQueries({ queryKey: PORTAL_KEY });
    },
    onError: (e) => message.error(errMsg(e, failure)),
  });
}

/** Block / unblock one of the agency's people (user or listener account). */
export function useBlockActions() {
  const block = usePortalAction<{ userId: string; reason: string }>(
    (v) => `/people/${v.userId}/block`,
    (v) => ({ reason: v.reason }),
    'Blocked — they were signed out of the app',
    'Could not block',
  );
  const unblock = usePortalAction<{ userId: string }>(
    (v) => `/people/${v.userId}/unblock`,
    () => ({}),
    'Unblocked',
    'Could not unblock',
  );
  return { block, unblock };
}

/**
 * A confirm dialog that needs a written reason (≥ 10 characters — the same
 * rule the API enforces, and what lands in the audit log).
 */
export function ReasonModal({
  open,
  title,
  description,
  okText,
  loading,
  onOk,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: string;
  okText: string;
  loading: boolean;
  onOk: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const short = reason.trim().length < 10;
  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      okButtonProps={{ danger: true, disabled: short, loading }}
      onOk={() => onOk(reason.trim())}
      onCancel={onCancel}
      afterClose={() => setReason('')}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary">{description}</Typography.Paragraph>
      <Input.TextArea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (at least 10 characters)"
        autoSize={{ minRows: 3, maxRows: 6 }}
        maxLength={500}
        showCount
      />
    </Modal>
  );
}
